import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $document } from "../document-list";
import { docsListItemLocalEntry } from "../document-list/document-list.types";
import { env } from "../env";
import { fs } from "../filesystem/filesystem.atom";
import { getDocumentDotMomentPath } from "../paths/paths.internal";

const logger = new Logger("template-preview-upload");

const PREVIEW_FILENAME = "template-preview.svg";

/** Convert raw SVG text to a data URL for display. */
function svgToDataUrl(svgContent: string): string {
	const encoded = btoa(unescape(encodeURIComponent(svgContent)));
	return `data:image/svg+xml;base64,${encoded}`;
}

// Optimistic draft: holds the latest uploaded data URL so the UI updates immediately.
// undefined = not yet written this session (fall through to disk read).
const $previewDraft = atomFamily((_documentId: string) => {
	const draftAtom = atom(undefined as string | null | undefined);
	draftAtom.debugPrivate = true;
	return draftAtom;
});

/**
 * Reads the local `.moment/template-preview.svg` and returns a data URL,
 * or null if it doesn't exist.
 *
 * Priority: optimistic draft (set after upload) → disk read → null.
 */
export const $localTemplatePreviewImage = atomFamily((documentId: string | undefined) => {
	const $previewAtom = atom(async (get): Promise<string | null> => {
		if (!documentId || !env.isFilesystemAvailable) {
			return null;
		}

		// Return draft if we've already uploaded this session
		const draft = get($previewDraft(documentId));
		if (draft !== undefined) {
			return draft;
		}

		const doc = get($document(documentId));
		const local = doc ? docsListItemLocalEntry(doc) : undefined;
		if (!local) {
			return null;
		}

		try {
			const dotMomentPath = await getDocumentDotMomentPath(documentId);
			const previewPath = await (
				await import("@tauri-apps/api/path")
			).join(dotMomentPath, PREVIEW_FILENAME);

			const existsAtom = fs().exists({ filePath: previewPath });
			const exists = await get(existsAtom);
			if (!exists) {
				return null;
			}

			const textAtom = fs().readTextFile({ filePath: previewPath });
			const svgContent = await get(textAtom);
			return svgToDataUrl(svgContent);
		} catch (err) {
			logger.debug("Failed to read local template preview", { documentId, err });
			return null;
		}
	});
	$previewAtom.debugLabel = `localTemplatePreviewImage/${documentId}`;
	return $previewAtom;
});

/**
 * Write-only atom that writes an SVG string to `.moment/template-preview.svg`
 * and optimistically updates the in-memory draft so the UI refreshes instantly.
 */
export const $uploadTemplatePreview = atom(
	null,
	async (_get, set, params: { documentId: string; svgContent: string }) => {
		const { documentId, svgContent } = params;

		// Basic SVG validation
		const trimmed = svgContent.trim();
		if (!trimmed.includes("<svg")) {
			throw new Error("Content does not appear to be valid SVG");
		}

		const dotMomentPath = await getDocumentDotMomentPath(documentId);
		const previewPath = await (
			await import("@tauri-apps/api/path")
		).join(dotMomentPath, PREVIEW_FILENAME);

		// Ensure .moment directory exists
		await set(fs().mkdir, { dirPath: dotMomentPath });

		await set(fs().writeTextFile, {
			filePath: previewPath,
			content: svgContent,
			documentId,
		});

		// Update draft only after disk write succeeds so the UI never shows unsaved data
		set($previewDraft(documentId), svgToDataUrl(svgContent));

		logger.info("Uploaded template preview image", { documentId });
	}
);
$uploadTemplatePreview.debugLabel = "uploadTemplatePreview";
